import React from 'react';
import { styled } from '@mui/material';
import { Colors, LoadingIndicator, OutlineButton, Text } from 'library';
import { ProviderRow } from './ProviderRow';

import type { IProviderSearch } from '../models';

export function Results({
    loading,
    error,
    providers,
    selectedProviders,
    handleSelect,
    loadMore,
    handleChange,
}: {
    loading: boolean;
    error: boolean;
    providers: IProviderSearch[];
    selectedProviders: IProviderSearch[];
    handleSelect: (checked: boolean, provider: IProviderSearch) => void;
    loadMore: boolean;
    handleChange: (value: string | boolean, formKey: string) => void;
}) {
    if (error)
        return (
            <EmptyState>
                <Text>
                    Sorry, something went wrong — please try again. If the problem continues, please contact support.
                </Text>
            </EmptyState>
        );

    return (
        <Container>
            {loading && (
                <LoadingOverlay>
                    <LoadingIndicator size={32} />
                </LoadingOverlay>
            )}
            <ResultsContainer style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s ease' }}>
                {providers.length ? (
                    providers.map((provider: IProviderSearch) => (
                        <ProviderRow
                            key={provider.id}
                            provider={provider}
                            checked={selectedProviders.includes(provider)}
                            handleCheck={(checked: boolean) => handleSelect(checked, provider)}
                        />
                    ))
                ) : !loading ? (
                    <EmptyState>
                        <Text variant="body1" textStyle={{ color: Colors.mediumNavy }}>
                            No workers found matching your filters.
                        </Text>
                    </EmptyState>
                ) : null}
            </ResultsContainer>
            <StyledBottom>
                {!loading && loadMore && (
                    <OutlineButton
                        onClick={() => handleChange(true, 'page')}
                        buttonStyle={{ padding: '8px 32px', borderRadius: 8 }}
                    >
                        Load More
                    </OutlineButton>
                )}
            </StyledBottom>
        </Container>
    );
}

const Container = styled('div')({
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    position: 'relative',
});

const LoadingOverlay = styled('div')({
    position: 'absolute',
    top: 40,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    zIndex: 1,
});

const ResultsContainer = styled('div')({
    width: '100%',
});

const EmptyState = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 20px',
});

const StyledBottom = styled('div')({
    marginTop: 16,
    marginBottom: 40,
});
